import { Dispatch, SetStateAction, useCallback, useState } from "react"
import { FileRejection } from "react-dropzone"

import { postClientProtected } from "../../clients/clients"
import {
    IPostAttchResponse,
    IRejectedFiles,
    IUpload,
    IUploadData,
    IUploadResult,
} from "../../types/singleApplication/uploadAttachmentTypes"
import { INSTALLER_ATTACHMENT_UPLOAD } from "../../urls/hybrid"

const IMAGE_FILE_TYPE = "image"
const ALLOWED_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/heic"]
const ALLOWED_IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "heic"]

const isSupportedImageFile = (file: File): boolean => {
    const fileType = file.type?.toLowerCase()
    if (fileType && ALLOWED_IMAGE_MIME_TYPES.includes(fileType)) {
        return true
    }

    const fileExtension = file.name?.split(".").pop()?.toLowerCase()
    return !!fileExtension && ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension)
}

type AllowedImageKind = "png" | "jpeg" | "known" | "unknown"

const startsWithBytes = (bytes: Uint8Array, signature: number[]): boolean =>
    signature.every((value, index) => bytes[index] === value)

const readMagicBytes = async (file: File, length = 32): Promise<Uint8Array> => {
    const buffer = await file.slice(0, length).arrayBuffer()
    return new Uint8Array(buffer)
}

const detectAllowedImageKindByMagicBytes = async (
    file: File
): Promise<AllowedImageKind> => {
    const bytes = await readMagicBytes(file, 32)

    if (bytes.length >= 3 && startsWithBytes(bytes, [0xff, 0xd8, 0xff])) {
        return "jpeg"
    }

    if (
        bytes.length >= 8 &&
        startsWithBytes(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
    ) {
        return "png"
    }

    // HEIC and HEIF not checked as it is more complicated with its Magic Bytes
    const fileType = file.type?.toLowerCase()
    if (fileType === "image/heic") {
        return "known"
    }

    return "unknown"
}

const isSupportedImageFileByMagicBytes = async (
    file: File
): Promise<boolean> => {
    const kind = await detectAllowedImageKindByMagicBytes(file)
    return kind !== "unknown"
}
interface IUploadAttachments {
    deleteUpload: (nameToRemove: string) => void
    uploads: IUpload[]
    onDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[]) => void
    rejectedUploads: IRejectedFiles[]
    clearRejections: () => void
}

/**
 * Hook for installer attachment upload.
 *
 * Side effects are called alongside the upload state being updated
 *
 * @returns {IUploadAttachments} An object containing:
 * - `uploadAttachment`: A function that takes a base64 image string and an
 *    upload name, sends a POST request to upload the image, and updates the
 *    `uploads` state.
 * - `uploads`: An array of `IUpload` objects representing the current uploads.
 * - `setUploads`: A function to update the `uploads` state.
 *
 */
const useUploadAttachments = (
    uploads: IUpload[],
    setUploads: Dispatch<SetStateAction<IUpload[]>>,
    maxPermittedFiles: number,
    imageMode: boolean,
    commissioning?: boolean,
    uploadSideEffects?: (newStuff: IUpload[]) => void
): IUploadAttachments => {
    const [rejectedUploads, setRejectedUploads] = useState<IRejectedFiles[]>([])

    /** Deletes attachment from UI */
    const deleteUpload = (nameToRemove: string): void => {
        setUploads((existingUploads: IUpload[]) => {
            const newUploads = existingUploads.filter(
                (upload: IUpload) => upload.name !== nameToRemove
            )
            uploadSideEffects?.(newUploads)
            return newUploads
        })
    }

    const clearRejections = (): void => {
        setRejectedUploads([])
    }

    /** Uploads attachments to S3 */
    const uploadAttachment = useCallback(
        async (uploadData: IUploadData[]): Promise<IUploadResult[]> => {
            const uploadPromises = uploadData.map(({ base64Img, uploadName }) =>
                postClientProtected(INSTALLER_ATTACHMENT_UPLOAD, {
                    file_type: IMAGE_FILE_TYPE,
                    file_name: uploadName,
                    file_data: base64Img,
                })
                    .then((res) => {
                        // If not type safe then will be caught by catch
                        const resTyped = res as IPostAttchResponse
                        if (!resTyped?.file_url) {
                            throw new Error(
                                `file_url not return for ${uploadName}`
                            )
                        }
                        return {
                            uploadName,
                            status: "fulfilled" as const,
                            fileUrl: resTyped.file_url,
                        }
                    })
                    .catch(() => ({
                        uploadName,
                        status: "rejected" as const,
                    }))
            )

            const results = await Promise.allSettled(uploadPromises)
            return results.map((result, index) => {
                if (result.status === "fulfilled") {
                    return result.value
                }
                return {
                    uploadName: uploadData[index].uploadName,
                    status: "rejected" as const,
                }
            })
        },
        []
    )

    /**
     * Handles files once dropped into a react dropzone box. It collects
     * immediately rejected files, asynchronously reads the files into the UI,
     * and then posts the files to the backend. Uploaded files are immediately
     * added to the uploads state with a loading state of true. Once the files have been
     * uploaded, the loading state is reversed.
     */
    const onDrop = useCallback(
        async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            setRejectedUploads([])
            const rejections: IRejectedFiles[] = []

            if (acceptedFiles.length + uploads.length > maxPermittedFiles) {
                rejections.push({
                    name: "Too many files",
                    reason: `You can only upload ${maxPermittedFiles} file${
                        maxPermittedFiles > 1 ? "s" : ""
                    } for this input`,
                })
                setRejectedUploads(rejections)
                return
            }

            const acceptedFilesCheckedWithValidation = await Promise.all(
                acceptedFiles.map(async (file) => {
                    if (!imageMode || commissioning) {
                        return { file, isAccepted: true }
                    }

                    const matchesMimeTypeOrExtension =
                        isSupportedImageFile(file)
                    const matchesMagicBytes =
                        await isSupportedImageFileByMagicBytes(file)

                    return {
                        file,
                        isAccepted:
                            matchesMimeTypeOrExtension && matchesMagicBytes,
                    }
                })
            )

            const acceptedFilesChecked = acceptedFilesCheckedWithValidation
                .filter(({ file, isAccepted }) => {
                    if (!isAccepted) {
                        rejections.push({
                            name: file?.name,
                            reason: "Only common image file types such as PNG or JPEG are supported",
                        })
                    }
                    return isAccepted
                })
                .map(({ file }) => file)
            // // If an file already exists in uploads, reject its
            // const acceptedFilesChecked = acceptedFiles.filter((file) => {
            //     const alreadyUploaded = uploads.find(
            //         (upload) => upload.name === file.name
            //     )

            //     if (alreadyUploaded) {
            //         rejections.push({
            //             name: file?.name,
            //             reason: "A file with this name already exists",
            //         })
            //         return false
            //     }
            //     return true
            // })

            // Collect files rejected by react dropzone
            rejectedFiles.forEach((file) => {
                let reason = ""
                try {
                    reason = file?.errors[0]?.message
                } catch {
                    reason = "We were unable to read this file"
                }
                rejections.push({
                    name: file?.file?.name,
                    reason,
                })
            })

            // Add all files to state so they can be displayed with loading state
            const newUploads: IUpload[] = acceptedFilesChecked.map((file) => ({
                name: file.name,
                size: file.size,
                loading: true,
                preview: URL.createObjectURL(file),
                fileUrl: undefined,
            }))
            setUploads((existingUploads: IUpload[]) => {
                const newUploadsState = [...existingUploads, ...newUploads]
                uploadSideEffects?.(newUploadsState)
                return newUploadsState
            })

            // Read files into UI
            const uploadData = await Promise.allSettled(
                acceptedFilesChecked.map((file) => {
                    const reader = new FileReader()
                    const promise = new Promise<IUploadData>(
                        (resolve, reject) => {
                            reader.onload = () => {
                                if (reader.result) {
                                    resolve({
                                        base64Img: reader.result.toString(),
                                        uploadName: file.name,
                                    })
                                } else {
                                    rejections.push({
                                        name: file?.name,
                                        reason: "Failed to read file",
                                    })
                                    reject(
                                        new Error(
                                            `Failed to read file ${file?.name}`
                                        )
                                    )
                                }
                            }
                            reader.onerror = () => {
                                rejections.push({
                                    name: file?.name,
                                    reason: "Failed to read file",
                                })
                                reject(
                                    new Error(
                                        `Failed to read file ${file?.name}`
                                    )
                                )
                            }
                        }
                    )
                    reader.readAsDataURL(file)
                    return promise
                })
            )

            // Collect files that were successfully read
            const successfulUploadData = uploadData
                .filter(
                    (result): result is PromiseFulfilledResult<IUploadData> =>
                        result.status === "fulfilled"
                )
                .map((result) => result.value)

            // Post successful files to backend
            const results = await uploadAttachment(successfulUploadData)

            // Add names of files rejected by uploadAttachment to uploadRejectedFiles
            results.forEach((result) => {
                if (result.status !== "fulfilled") {
                    rejections.push({
                        name: result.uploadName,
                        reason: "We were unable to upload this file to the server",
                    })
                }
            })

            // Update state with loading. Any failed uploads will be removed from state
            // as their loading state will be false
            setUploads((currentUploads) => {
                const newUploadsState = currentUploads
                    .map((upload) => {
                        const result = results.find(
                            (res) => res.uploadName === upload.name
                        )
                        if (result && result.status === "fulfilled") {
                            return {
                                ...upload,
                                loading: false,
                                fileUrl: result.fileUrl,
                            }
                        }
                        return upload
                    })
                    .filter(
                        (upload) =>
                            !rejections
                                .map((rejection) => rejection.name)
                                .includes(upload.name)
                    )
                uploadSideEffects?.(newUploadsState)
                return newUploadsState
            })
            setRejectedUploads(rejections)
        },
        [
            maxPermittedFiles,
            setUploads,
            uploadAttachment,
            uploadSideEffects,
            uploads.length,
            imageMode,
            commissioning,
        ]
    )

    return {
        onDrop,
        deleteUpload,
        uploads,
        rejectedUploads,
        clearRejections,
    }
}

export default useUploadAttachments
